import React from "react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";

/**
 * Sortable, drag-and-drop list of zones.
 *  items: [{ id, label, subtitle? }]
 *  onChange: (nextIds: string[]) => void
 *  onRemove?: (id: string) => void
 */
export default function SortableZoneList({ items, onChange, onRemove, testIdPrefix = "sortable-zone", emptyMessage = "No zones selected yet." }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((it) => it.id === active.id);
    const newIdx = items.findIndex((it) => it.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onChange(arrayMove(items, oldIdx, newIdx).map((it) => it.id));
  };
  if (items.length === 0) {
    return <div className="text-sm text-[color:var(--text-secondary)] p-3 rounded border border-dashed border-[color:var(--border)]">{emptyMessage}</div>;
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <ol className="space-y-2" data-testid={`${testIdPrefix}-list`}>
          {items.map((it, idx) => (
            <SortableRow key={it.id} item={it} index={idx} onRemove={onRemove} testIdPrefix={testIdPrefix}/>
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({ item, index, onRemove, testIdPrefix }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <li ref={setNodeRef} style={style}
        className="flex items-center gap-2 p-2 rounded-md border border-[color:var(--border)] bg-[color:var(--bg-surface-2)]"
        data-testid={`${testIdPrefix}-row-${item.id}`}>
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-[color:var(--text-secondary)] hover:text-[color:var(--gold)] p-1 touch-none"
        aria-label={`Drag ${item.label}`}
        data-testid={`${testIdPrefix}-handle-${item.id}`}
      >
        <GripVertical size={16}/>
      </button>
      <div className="w-6 text-[color:var(--gold)] font-heading">{index + 1}</div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm truncate">{item.label}</div>
        {item.subtitle && <div className="text-xs text-[color:var(--text-secondary)] truncate">{item.subtitle}</div>}
      </div>
      {onRemove && (
        <button type="button" onClick={() => onRemove(item.id)} className="text-[color:var(--error)] p-1" aria-label={`Remove ${item.label}`} data-testid={`${testIdPrefix}-remove-${item.id}`}>
          <X size={14}/>
        </button>
      )}
    </li>
  );
}
